import { Head } from "@inertiajs/react"
import { Badge, Card, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { newProjectImportPath, projectDashboardPath, projectImportPath } from "@/routes"
import { ImportSchema, ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  imports: z.array(ImportSchema),
})

type Props = z.infer<typeof PropsSchema>

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getStatusColor(status: string) {
  switch (status) {
    case "pending":
      return "yellow"
    case "ready_to_process":
      return "blue"
    case "processing":
      return "orange"
    case "completed":
      return "green"
    case "failed":
      return "red"
    default:
      return "gray"
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case "ready_to_process":
      return "Ready to process"
    default:
      return status.charAt(0).toUpperCase() + status.slice(1)
  }
}

export default function ImportsIndex(props: Props) {
  console.log(props)

  const validatedProps = PropsSchema.parse(props)
  const { imports, project } = validatedProps

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Imports" },
      ]}
    >
      <Head title="Imports" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Imports
          </Heading>

          <LinkButton href={newProjectImportPath(project.slug)}>New import</LinkButton>
        </Flex>

        <Text color="gray">Import customers from CSV files to add them to your project.</Text>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Lines</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email column</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {imports.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No imports found
                  </Table.Cell>
                </Table.Row>
              ) : (
                imports.map((importItem) => (
                  <Table.Row key={importItem.id}>
                    <Table.Cell>
                      <Badge color={getStatusColor(importItem.status)}>
                        {getStatusLabel(importItem.status)}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {importItem.file_count_lines
                        ? importItem.file_count_lines.toLocaleString()
                        : "-"}
                    </Table.Cell>
                    <Table.Cell>{formatDate(importItem.created_at)}</Table.Cell>
                    <Table.Cell>
                      {importItem.email_column_index !== null
                        ? `Column ${importItem.email_column_index}`
                        : "-"}
                    </Table.Cell>
                    <Table.Cell>
                      <LinkButton
                        href={projectImportPath(project.slug, importItem.id)}
                        variant="solid"
                        size="1"
                      >
                        View
                      </LinkButton>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
